import { useEffect, useState } from 'react'

import { CancelConfirmDialog } from '../cancel-confirm-dialog'
import {
  DEFAULT_CANCEL_CONFIRM_MESSAGE,
  DEFAULT_CANCEL_CONFIRM_NO_LABEL,
  DEFAULT_CANCEL_CONFIRM_YES_LABEL,
  DEFAULT_REJECT_MODAL_CANCEL_LABEL,
  DEFAULT_REJECT_MODAL_CHAR_LIMIT,
  DEFAULT_REJECT_MODAL_HELP_TEXT,
  DEFAULT_REJECT_MODAL_PLACEHOLDER,
  DEFAULT_REJECT_MODAL_REQUIRED_ERROR,
  DEFAULT_REJECT_MODAL_SEND_LABEL,
  DEFAULT_REJECT_MODAL_TITLE,
} from '../constants/labels'

export type CaseRejectModalProps = {
  readonly caseNumber?: string | null
  readonly caseSubject?: string | null
  readonly caseText?: string
  readonly subjectLabel?: string
  readonly title?: string
  readonly helpText?: string
  readonly placeholder?: string
  readonly charLimit?: number
  readonly cancelLabel?: string
  readonly sendLabel?: string
  readonly sendDisabled?: boolean
  readonly requiredError?: string
  readonly cancelConfirmMessage?: string
  readonly cancelConfirmYesLabel?: string
  readonly cancelConfirmNoLabel?: string
  readonly cancelConfirmSubtext?: string
  readonly onClose: () => void
  readonly onSubmit: (comment: string) => void
}

export default function CaseRejectModal({
  caseNumber,
  caseSubject,
  caseText,
  subjectLabel,
  title = DEFAULT_REJECT_MODAL_TITLE,
  helpText = DEFAULT_REJECT_MODAL_HELP_TEXT,
  placeholder = DEFAULT_REJECT_MODAL_PLACEHOLDER,
  charLimit = DEFAULT_REJECT_MODAL_CHAR_LIMIT,
  cancelLabel = DEFAULT_REJECT_MODAL_CANCEL_LABEL,
  sendLabel = DEFAULT_REJECT_MODAL_SEND_LABEL,
  sendDisabled,
  requiredError = DEFAULT_REJECT_MODAL_REQUIRED_ERROR,
  cancelConfirmMessage = DEFAULT_CANCEL_CONFIRM_MESSAGE,
  cancelConfirmYesLabel = DEFAULT_CANCEL_CONFIRM_YES_LABEL,
  cancelConfirmNoLabel = DEFAULT_CANCEL_CONFIRM_NO_LABEL,
  cancelConfirmSubtext,
  onClose,
  onSubmit,
}: CaseRejectModalProps) {
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const handleRequestClose = () => {
    if (comment.trim()) {
      setShowCancelConfirm(true)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCancelConfirm) {
          setShowCancelConfirm(false)
        } else if (comment.trim()) {
          setShowCancelConfirm(true)
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, showCancelConfirm, comment])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= charLimit) {
      setComment(e.target.value)
      if (e.target.value.trim()) setError('')
    }
  }

  const handleBlur = () => {
    if (!comment.trim()) setError(requiredError)
  }

  const handleSend = () => {
    if (!comment.trim()) {
      setError(requiredError)
      return
    }
    onSubmit(comment.trim())
  }

  return (
    <dialog className="cop-modal-overlay" aria-modal="true" aria-labelledby="cop-reject-modal-title">
      <div className="cop-modal">

        <div className="cop-modal__header">
          <div>
            <h2 id="cop-reject-modal-title" className="cop-modal__title">{title}</h2>
            {caseNumber && (
              <p className="cop-modal__help-text">
                {caseText ? `${caseText} #${caseNumber}` : caseNumber}
              </p>
            )}
          </div>
          <button type="button" className="cop-modal__close" aria-label="Close" onClick={handleRequestClose}>
            &#x2715;
          </button>
        </div>

        {caseSubject && (
          <p className="cop-modal__help-text">
            <strong>{subjectLabel ? `${subjectLabel}:` : 'Case Subject:'}</strong> {caseSubject}
          </p>
        )}

        {helpText && (
          <p className="cop-modal__help-text">{helpText}</p>
        )}
        <div className="cop-modal__textarea-wrap">
          <textarea
            className={`cop-modal__textarea${error ? ' cop-modal__textarea--error' : ''}`}
            placeholder={placeholder}
            value={comment}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={5}
            maxLength={charLimit}
          />
          <span className="cop-modal__char-count">{comment.length}/{charLimit}</span>
        </div>
        {error && (
          <p className="field-error" role="alert">
            <span className="field-error__icon">i</span>
            {error}
          </p>
        )}

        <div className="cop-modal__footer">
          <button type="button" className="cop-modal__btn cop-modal__btn--share" onClick={handleSend} disabled={sendDisabled || !comment.trim()}>
            {sendLabel}
          </button>
          <button type="button" className="cop-modal__btn cop-modal__btn--cancel" onClick={handleRequestClose}>
            {cancelLabel}
          </button>
        </div>

      </div>

      {showCancelConfirm && (
        <CancelConfirmDialog
          message={cancelConfirmMessage}
          yesLabel={cancelConfirmYesLabel}
          noLabel={cancelConfirmNoLabel}
          subtext={cancelConfirmSubtext}
          onConfirm={onClose}
          onDismiss={() => setShowCancelConfirm(false)}
        />
      )}
    </dialog>
  )
}
